import { cn } from '../lib/utils';
import { getRegionConfig } from '../lib/regionConfig';

// What each plan does for the customer. `promise` is the one-line benefit
// under the plan's name; each feature is written as what it gets them. A
// higher plan lists only what it adds, after "Everything in <lower plan>".
export const FOUNDING_PROMISE = 'Know when every camera, lens and light has paid for itself.';

export const FOUNDING_FEATURES = [
  'Unlimited jobs and estimates',
  'Profit and margin on every job, worked out for you',
  'See which gear pays for itself, with return on each item',
  'Send jobs to Xero or a CSV, ready for your accounting software to invoice',
  'Estimates that look the part',
];

/**
 * The plans not on sale yet. A function of the region because crew super
 * exists only where the region defines it (Australia today), so the line is
 * left out everywhere else rather than promised to someone it cannot apply to.
 */
export const comingTiers = (region) => {
  const crewSuper = getRegionConfig(region).crewSuper;
  return [
    {
      key: 'projects',
      name: 'Filmmaker',
      promise: 'Tools to help win the work.',
      includes: 'Solo',
      features: [
        'Send the client a link to a brief, and their answers come straight back into it',
        'Show the client the look, shot by shot, in a visual shot list',
      ],
    },
    {
      key: 'producer',
      name: 'Teams',
      promise: 'Manage a small crew and deliver on your promise.',
      includes: 'Filmmaker',
      features: [
        'Book your crew onto the job, day by day, at their rates',
        'Hire the gear you don’t own from your suppliers',
        'A purchase order for every crew member in one click',
        'Crew bills go to Xero',
        ...(crewSuper ? [`${crewSuper.name} worked out for your crew`] : []),
      ],
    },
  ];
};

/**
 * One plan on the Billing tab, in the design system's tier card: the plan's
 * name and one-line promise, its price large and light, a dashed list of what
 * it includes, then its actions at the foot.
 *
 * Those four parts are rows of the parent grid (`grid-rows-subgrid`), so in a
 * row of cards each part starts at the same height whatever wraps above it:
 * the prices line up, and so do the lists. The parent is `PlanTierRow`.
 *
 * `featured` prints it on the inverse band (the plan on sale). `price` is
 * { amount, was, period }; `was` is struck through beside the amount. A plan
 * not priced yet shows a dash. `comingSoon` fades the card. `children` holds
 * the actions: the subscribe button, the onboarding add-on.
 */
export const PlanTierCard = ({
  name, promise, features, includes, price, featured = false, comingSoon = false, children,
}) => (
  <article
    className={cn(
      'grid grid-rows-subgrid row-span-4 gap-y-4 p-6 min-w-0',
      featured ? 'bg-inverse text-inverse-foreground rounded-highlight' : 'bg-surface rounded-tile',
      comingSoon && 'opacity-70',
    )}
    data-testid={`plan-tier-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
  >
    <div className="flex flex-col gap-2">
      <h3 className="section-title">{name}</h3>
      {promise && <p className="text-[17px] leading-snug">{promise}</p>}
    </div>

    <p className="flex flex-wrap items-baseline gap-x-3 leading-none">
      {price ? (
        <>
          {price.was && (
            <s className={cn('text-2xl font-light tabular-nums', featured ? 'text-inverse-muted' : 'text-muted-foreground')}>
              <span className="sr-only">Was </span>{price.was}
            </s>
          )}
          <span className="text-[56px] font-light tracking-tight tabular-nums">{price.amount}</span>
          <span className={cn('text-base', featured ? 'text-inverse-muted' : 'text-muted-foreground')}>{price.period}</span>
        </>
      ) : (
        <span className={cn('text-[56px] font-light', featured ? 'text-inverse-muted' : 'text-muted-foreground')} aria-label="Not priced yet">—</span>
      )}
    </p>

    <ul className="flex flex-col gap-2 text-[15px]">
      {includes && <Item featured={featured}>Everything in {includes}</Item>}
      {features.map((feature) => <Item key={feature} featured={featured}>{feature}</Item>)}
    </ul>

    {/* Always rendered, so every card fills the same four rows */}
    <div className="self-end flex flex-col gap-3">{children}</div>
  </article>
);

/** The row of plan cards: one shared grid whose rows each card subdivides. */
export const PlanTierRow = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{children}</div>
);

const Item = ({ featured, children }) => (
  <li className="flex gap-2">
    <span aria-hidden="true" className={featured ? 'text-inverse-muted' : 'text-ink-faded'}>—</span>
    <span>{children}</span>
  </li>
);
